import type { BacktestFormState, BacktestSetupSnapshot, HistoryItem } from "./types.ts";
import { localDraftKey } from "./local-draft.ts";
import { backtestProviderForSymbol } from "./api.ts";

const WEB_PAPER_TIMEFRAMES = new Set(["5m", "15m", "30m", "1h", "4h", "1d"]);
const record = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);
const isBinanceSymbol = (value: unknown): value is string =>
  typeof value === "string" && backtestProviderForSymbol(value.trim()) === "binance";

/** The web form cannot represent these native-only settings faithfully yet. */
export function supportsPaperHistorySetup(setup: unknown): setup is BacktestSetupSnapshot {
  return record(setup) && record(setup.strategyDSL)
    && isBinanceSymbol(setup.symbol)
    && (setup.market == null || setup.market === "crypto")
    && (setup.provider == null || setup.provider === "binance")
    && (setup.timeframe === undefined || typeof setup.timeframe === "string" && WEB_PAPER_TIMEFRAMES.has(setup.timeframe))
    && (setup.riskMode === undefined || setup.riskMode === "auto")
    && (setup.positionSizing === undefined || setup.positionSizing === "notional")
    && (setup.maxLeverage === undefined || setup.maxLeverage === 1)
    && (setup.executionModel === undefined || setup.executionModel === "spot")
    && (setup.tradeDirection === undefined || setup.tradeDirection === "long");
}

/** The legacy history cache accepts arbitrary JSON arrays; validate only the new Paper projection. */
export function paperHistorySourceItems(items: readonly unknown[]): HistoryItem[] {
  return items.slice(0, 30).filter((item): item is HistoryItem => {
    if (!record(item) || typeof item.id !== "string" || !item.id.trim()
      || typeof item.createdAt !== "string" || typeof item.prompt !== "string"
      || !supportsPaperHistorySetup(item.setup)) return false;
    const setup = item.setup;
    const textFields = ["symbol", "backtestPeriod", "strategyMode", "strategyName", "prompt", "marginMode", "exitMode"] as const;
    const numberFields = ["initialCapital", "commissionPercent", "slippageBps", "positionSizePercent", "leverage",
      "maintenanceMarginPercent", "fundingRatePercent", "fundingIntervalHours", "liquidationFeePercent",
      "stopLossPercent", "takeProfitPercent"] as const;
    return textFields.every((key) => setup[key] === undefined || typeof setup[key] === "string")
      && numberFields.every((key) => setup[key] === undefined || typeof setup[key] === "number" && Number.isFinite(setup[key]))
      && [setup.startDate, setup.endDate].every((value) => value === undefined || value === null || typeof value === "string")
      && (setup.useAI === undefined || typeof setup.useAI === "boolean");
  });
}

export type PaperAppliedSourceState = "pending" | "ready" | "blocked";

/** History imports may block the applied source; later edits/imports cannot erase that provenance. */
export function paperSourceAfterHistory(current: PaperAppliedSourceState, setup: BacktestSetupSnapshot): PaperAppliedSourceState {
  return current === "blocked" || !supportsPaperHistorySetup(setup) ? "blocked" : "ready";
}

/** Only an explicit compatible builder/library/Community selection establishes a new applied source. */
export function paperSourceAfterExplicitSelection(current: PaperAppliedSourceState, form: BacktestFormState): PaperAppliedSourceState {
  return form.strategyMode === "builder" && Boolean(form.strategyDSL)
    && form.executionModel === "spot" && form.tradeDirection === "long"
    && isBinanceSymbol(form.symbol)
    && WEB_PAPER_TIMEFRAMES.has(form.timeframe) ? "ready" : current;
}

type SourceStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;
const blockKey = (origin: string, accountID: string | null) => `backtest.paper.history-block.v1.${localDraftKey(origin, accountID)}`;

/** Persist only a flag in the draft's API/account scope, so restoring a stripped form cannot bypass the block. */
export function readPaperHistoryBlock(storage: SourceStorage | null, origin: string, accountID: string | null): boolean {
  try { return storage ? storage.getItem(blockKey(origin, accountID)) !== null : true; }
  catch { return true; }
}

export function writePaperHistoryBlock(storage: SourceStorage | null, origin: string, accountID: string | null, blocked: boolean): void {
  try {
    if (blocked) storage?.setItem(blockKey(origin, accountID), "1");
    else storage?.removeItem(blockKey(origin, accountID));
  } catch { /* The current account's in-memory gate still applies when persistence is unavailable. */ }
}
